<template>
  <form
    autocomplete="off"
    class="gap-0 gap-y-4"
    @submit.prevent="submit"
  >
    <div class="col-span-12 mx-auto text-center">
      <div>
        <h2>
          Confirmation Code
        </h2>

        <span class="mb-8 block text-gray-dark font-medium">
          We’ve just sent you a 6-digit confirmation code. Please check your messages and enter it below.
        </span>

        <span class="mb-7 block text-sm">
          Can’t find it? Check your spam folder, or click to reset.
        </span>

        <TextInput
          v-model="formData.code"
          label="Enter your Confirmation Code"
          required
          class="mb-8"
          :validation-error="errors"
          autofocus
        />

        <AppButton
          :processing="isSubmitting"
          :disabled="cooldown !== 0"
          class="btn btn-solid py-2.5 w-full mb-6"
          :class="{'btn-gray': (cooldown > 0), 'btn-secondary': (cooldown < 1), }"
        >
          {{ isSubmitting ? 'Confirming' : 'Confirm' }}
        </AppButton>

        <AppButton
          class="btn py-2.5 w-full mb-6 bg-gray-dark text-white hover:bg-primary"
          type="button"
          :disabled="cooldown !== 0"
          @click="requestAnotherCode"
        >
          {{ cooldown === 0 ? 'Request another code' : `Request another code (${cooldown})` }}
        </AppButton>
      </div>

      <template v-if="error">
        <span class="text-red-500 p-2 bg-red-100 rounded block">
          {{ error }}
        </span>
      </template>

      <template v-if="emailResent">
        <span
          class="text-green-700 border border-green-700 bg-[#0F7E6738] rounded flex items-center justify-center gap-2 p-2"
        >
          <FontAwesomeIcon :icon="faCircleCheck" />
          A new confirmation code has been sent to your email.
        </span>
      </template>
    </div>
  </form>
</template>

<script setup>
import { reactive, ref } from 'vue';
import { HttpStatusCode } from 'axios';

import { AppButton } from '@/components/app';
import { TextInput } from '@/components/forms/inputs';

import { useFormSubmit, useHttp } from '@/composables';
import { useAuthStore, useCartStore } from '@/store/index';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const authStore = useAuthStore();
const cartStore = useCartStore();

const { errors, error } = useFormSubmit();

const isSubmitting = defineModel('isSubmitting');

const emit = defineEmits(['close', 'submit']);

const cooldown = ref(0);

const props = defineProps({
  email: {
    type: String,
    default: null,
  },
});

const formData = reactive({
  code: null,
  email: props.email,
});

const submit = async () => {
  errors.value = null;
  emailResent.value = false;
  isSubmitting.value = true;

  const response = await useHttp().post('/auth/login/token', formData);

  if (response.statusCode === HttpStatusCode.Ok) {
    authStore.user = response.data;
    cartStore.loadCart();

    close();
    emit('submit');
  } else if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
    errors.value = response.errors.code[0];
  } else if (response.statusCode === HttpStatusCode.TooManyRequests) {
    error.value = response.exception;
    isSubmitting.value = false;

    cooldown.value = error.value.replace(/.*?(\d+).*/, '$1');
    error.value = error.value.split('.')[0];

    while (cooldown.value > 0) {
      await new Promise(resolve => {
        setTimeout(() => {
          cooldown.value -= 1;

          resolve();
        }, 1000);
      });
    }
  } else {
    errors.value = response.exception;
  }

  isSubmitting.value = false;
};

const emailResent = ref(false);

const requestAnotherCode = async () => {
  errors.value = null;
  error.value = null;
  emailResent.value = false;

  const response = await useHttp().post('/auth/login', { email: props.email });

  if (response.statusCode === HttpStatusCode.Ok) {
    emailResent.value = true;
  } else if (response.statusCode === HttpStatusCode.TooManyRequests) {
    error.value = response.exception;
    isSubmitting.value = false;

    cooldown.value = error.value.replace(/.*?(\d+).*/, '$1');
    error.value = error.value.split('.')[0];

    while (cooldown.value > 0) {
      await new Promise(resolve => {
        setTimeout(() => {
          cooldown.value -= 1;

          resolve();
        }, 1000);
      });
    }
  } else {
    errors.value = response.exception;
  }
};

const close = () => {
  emit('close');
};

</script>
